import React, { useState } from "react";
import { Link, useOutletContext } from "react-router";
import {
  AddDocumentIcon,
  CannedTestCreateIcon,
  CannedTestListIcon,
  PassageIcon,
  PencilIcon,
  QuestionIcon,
} from "../../../../assets/icons/index";
import Constant from "../../../../utils/constant";
import { IUserContentOutletContext } from "./UserContentLayout";
import { NewPassageModal } from "./NewPassageModal";

interface ITileGroup {
  heading: string;
  tiles: Array<{
    title: string;
    subtitle: string;
    to?: string;
    onClick?: () => void;
    Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  }>;
}

export const UserContent: React.FC = () => {
  const { userId } = useOutletContext<IUserContentOutletContext>();
  const withUser = (path: string) =>
    path.replace(":userid", String(userId ?? ""));
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const tileGroups: Array<ITileGroup> = [
    {
      heading: "Passages",
      tiles: [
        {
          title: "Passage Bank",
          subtitle: "Browse and manage existing passages",
          to: withUser(Constant.crmUserContentPassages),
          Icon: PassageIcon,
        },
        {
          title: "Create Passage",
          subtitle: "Author a new passage from scratch",
          onClick: () => setShowCreateModal(true),
          Icon: AddDocumentIcon,
        },
      ],
    },
    {
      heading: "Questions",
      tiles: [
        {
          title: "Question Bank",
          subtitle: "Browse and manage existing questions",
          to: withUser(Constant.crmUserContentQuestions),
          Icon: QuestionIcon,
        },
        {
          title: "Question Builder",
          subtitle: "Author a new question",
          to: withUser(Constant.crmUserContentQuestionCreate),
          Icon: PencilIcon,
        },
      ],
    },
    {
      heading: "Assessments",
      tiles: [
        {
          title: "Assessment Bank",
          subtitle: "Browse and manage existing assessments",
          to: withUser(Constant.crmUserContentAssessments),
          Icon: CannedTestListIcon,
        },
        {
          title: "Assessment Builder",
          subtitle: "Author a new assessment",
          to: withUser(Constant.crmUserContentAssessmentCreate),
          Icon: CannedTestCreateIcon,
        },
      ],
    },
  ];

  return (
    <>
      {showCreateModal && (
        <NewPassageModal
          userId={userId}
          onClose={() => setShowCreateModal(false)}
        />
      )}
      <div className="px-8 py-8 space-y-10 max-w-3xl">
        {tileGroups.map((group) => (
          <section key={group.heading}>
            <h4 className="text-base font-semibold text-gray-800 mb-4">
              {group.heading}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {group.tiles.map((tile) => (
                <TileCard
                  key={tile.title}
                  title={tile.title}
                  subtitle={tile.subtitle}
                  to={tile.to}
                  onClick={tile.onClick}
                  Icon={tile.Icon}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
};

interface ITileCardProps {
  title: string;
  subtitle: string;
  to?: string;
  onClick?: () => void;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}

const tileClassName =
  "group flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-8 shadow-sm transition hover:border-primary-color hover:shadow-lg";

const TileCardContent: React.FC<
  Pick<ITileCardProps, "title" | "subtitle" | "Icon">
> = ({ title, subtitle, Icon }) => (
  <>
    <Icon className="h-10 w-10 shrink-0 text-primary-color" />
    <div className="flex flex-col">
      <span className="text-lg font-semibold text-gray-900 group-hover:text-primary-color">
        {title}
      </span>
      <span className="text-sm text-gray-500 mt-1">{subtitle}</span>
    </div>
  </>
);

const TileCard: React.FC<ITileCardProps> = ({
  title,
  subtitle,
  to,
  onClick,
  Icon,
}) => {
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={tileClassName}>
        <TileCardContent title={title} subtitle={subtitle} Icon={Icon} />
      </button>
    );
  }
  return (
    <Link to={to!} className={tileClassName}>
      <TileCardContent title={title} subtitle={subtitle} Icon={Icon} />
    </Link>
  );
};
